import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { buildDeliveryDateTime, useTicker } from "../lib/time";
import { fmtDate } from "../lib/auditFormat";
import {
  Wrench, BadgeCheck, ArrowRight, AlertTriangle, Clock, Truck,
  Calendar, Pause, ChevronRight, User as UserIcon, PauseCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import FilterBar from "../components/FilterBar";
import ExportButton from "../components/ExportButton";
import { TransitHoldBadge } from "../components/TransitHoldBadge";
import { TransitHoldList } from "../components/TransitHoldList";
import { defaultFilters, buildApiParams, deriveScope, deriveMonthLabel } from "../lib/filters";

// ─── SLA timer helpers ──────────────────────────────────────────────
function fmtMins(min) {
  const abs = Math.abs(min);
  const d = Math.floor(abs / 1440);
  const h = Math.floor((abs % 1440) / 60);
  const m = Math.round(abs % 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/**
 * SLA timer descriptor.
 * tone:  green (>50% remaining) · amber (≤50% remaining) · red (overdue)
 *        slate (paused) · orange (delay_marked by user)
 */
function getSlaTimer(task) {
  if (!task) return { tone: "slate", label: "—" };
  if (task.delay_marked) {
    return { tone: "orange", label: "Delayed by user", icon: AlertTriangle };
  }
  if (task.sla_paused) {
    return { tone: "slate", label: "Paused", icon: PauseCircle };
  }
  const sla = task.sla || {};
  const rem = sla.remaining_minutes;
  const budget = sla.budget_minutes;
  if (rem == null || budget == null) return { tone: "slate", label: "—", icon: Clock };
  if (rem < 0) return { tone: "red", label: `+${fmtMins(rem)} overdue`, icon: Clock };
  const pct = budget > 0 ? rem / budget : 0;
  return {
    tone: pct > 0.5 ? "green" : "amber",
    label: `Due in ${fmtMins(rem)}`,
    icon: Clock,
  };
}

const TIMER_TONES = {
  green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:  "bg-amber-50 text-amber-800 border-amber-200",
  red:    "bg-rose-50 text-rose-700 border-rose-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  slate:  "bg-slate-100 text-slate-700 border-slate-200",
};

function TimerPill({ task, size = "sm" }) {
  const t = getSlaTimer(task);
  const Icon = t.icon || Clock;
  const sizeCls = size === "md" ? "px-2.5 py-1 text-[12px]" : "px-2 py-0.5 text-[11px]";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-mono-data font-bold",
        sizeCls,
        TIMER_TONES[t.tone],
      )}
      data-testid={`timer-${t.tone}`}
      title={task?.sla_paused && task?.sla_pause_remarks ? `Paused — ${task.sla_pause_remarks}` : ""}
    >
      <Icon className="h-3 w-3" />
      {t.label}
    </span>
  );
}

// ─── KPI card — equal height + fixed alignment ──────────────────────
const KPI_TONES = {
  amber:   "bg-amber-50 border-amber-200",
  emerald: "bg-emerald-50 border-emerald-200",
  indigo:  "bg-indigo-50 border-indigo-200",
  rose:    "bg-rose-50 border-rose-200",
  slate:   "bg-slate-50 border-slate-200",
};
const KPI_ICON_TONES = {
  amber:   "text-amber-700",
  emerald: "text-emerald-700",
  indigo:  "text-indigo-700",
  rose:    "text-rose-700",
  slate:   "text-slate-600",
};

function KpiCard({ label, value, helper, Icon, tone = "slate", to, testid }) {
  const Comp = to ? Link : "div";
  return (
    <Comp
      to={to}
      data-testid={testid}
      className={cn(
        "flex h-[124px] flex-col justify-between rounded-lg border p-3.5 shadow-od-subtle transition-colors hover:border-primary/40 sm:h-[132px]",
        KPI_TONES[tone],
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 leading-tight">
          {label}
        </span>
        {Icon && (
          <Icon className={cn("h-4 w-4 flex-shrink-0", KPI_ICON_TONES[tone])} />
        )}
      </div>
      <div className="font-display text-[28px] sm:text-[32px] font-bold leading-none tracking-tight text-slate-900">
        {value}
      </div>
      <div className="text-[11px] font-medium text-slate-600 leading-tight line-clamp-2 min-h-[28px]">
        {helper}
      </div>
    </Comp>
  );
}

// ─── Queue row card ─────────────────────────────────────────────────
const STAGE_META = {
  PDI_VIN:   { label: "VIN", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  PDI_READY: { label: "Readiness", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
};
function StagePill({ type }) {
  const meta = STAGE_META[type] || { label: type, cls: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <span className={cn(
      "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
      meta.cls,
    )}>
      {meta.label}
    </span>
  );
}

function QueueCard({ task, sale, loc, dseName, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`pdi-row-${task.sale_id}`}
      className="group flex w-full items-stretch gap-3 rounded-lg border border-slate-200 bg-white p-3.5 text-left shadow-od-subtle transition-colors hover:border-primary/40 hover:bg-slate-50/60 h-full min-h-[148px]"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {/* Top row — SR + Stage pill */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <span className="font-mono-data text-[12px] font-semibold text-primary truncate">
              {task.sr_number || sale?.sr_number || "—"}
            </span>
            {sale?.transit_hold?.active && <TransitHoldBadge testid={`pdi-row-${task.sale_id}-transit`} />}
          </span>
          <StagePill type={task.type} />
        </div>

        {/* Customer */}
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-semibold text-[14px] text-slate-900 truncate">
            {sale?.customer?.name || "—"}
          </span>
          {sale?.customer?.phone && (
            <span className="text-[12px] tabular-nums text-slate-500">
              {sale.customer.phone}
            </span>
          )}
        </div>

        {/* Vehicle + Location */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-slate-600">
          <span className="truncate">
            {sale?.vehicle?.model || "—"}
            {(sale?.vehicle?.color_code || sale?.vehicle?.color) && (
              <> · {sale.vehicle.color_code || sale.vehicle.color}</>
            )}
          </span>
          {loc?.code && <span className="inline-flex items-center gap-0.5">· 📍 {loc.code}</span>}
        </div>

        {/* DSE + Delivery */}
        {(dseName || sale?.delivery?.promised_date) && (
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-slate-500">
            {dseName && (
              <span className="inline-flex items-center gap-1">
                <UserIcon className="h-3 w-3" /> {dseName}
              </span>
            )}
            {sale?.delivery?.promised_date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {fmtDate(sale.delivery.promised_date)}
                {sale.delivery.promised_time ? ` · ${sale.delivery.promised_time}` : ""}
              </span>
            )}
          </div>
        )}

        {/* SLA Timer */}
        <div className="mt-auto pt-1">
          <TimerPill task={task} />
        </div>
      </div>
      <span className="flex flex-shrink-0 items-center self-center text-slate-300 group-hover:text-primary">
        <ChevronRight className="h-5 w-5" />
      </span>
    </button>
  );
}

// ─── Page ────────────────────────────────────────────────────────────
export default function PdiQueue() {
  useTicker(60000); // refresh timers every minute
  const { activeLocationIds, locations, activeUser, users } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("vin");
  const [tasks, setTasks] = useState([]);
  const [salesById, setSalesById] = useState({});
  // KPI dataset: ALL pending PDI tasks for the active location(s), independent
  // of the currently-active tab and filter bar. Without this, the KPI numbers
  // mirror only the tab in view (Pending VIN drops to 0 on the Readiness tab).
  const [allPdiTasks, setAllPdiTasks] = useState([]);
  const [allPdiSalesById, setAllPdiSalesById] = useState({});
  const [readyTodayCount, setReadyTodayCount] = useState(0);
  const [transitCount, setTransitCount] = useState(0);
  const [filters, setFilters] = useState(defaultFilters());

  const locParam = useMemo(() => activeLocationIds.join(","), [activeLocationIds]);

  // Memoised CSV-export params + scope.
  const exportParams = useMemo(() => ({
    type: tab === "vin" ? "PDI_VIN" : tab === "ready" ? "PDI_READY" : undefined,
    role: !["vin", "ready"].includes(tab) ? "PDI" : undefined,
    status: "pending",
    location_ids: locParam,
    ...buildApiParams(filters),
  }), [tab, locParam, filters]);
  const exportScope = useMemo(
    () => deriveScope({
      visibleLocs: locations.filter((l) => activeLocationIds.includes(l.id)),
      allLocations: locations,
    }),
    [locations, activeLocationIds],
  );
  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));
  const usersById = Object.fromEntries((users || []).map((u) => [u.id, u]));
  const userName = (activeUser?.name || "").split(" ")[0] || "there";

  const canAct = activeUser?.role === "PDI" || activeUser?.role === "Admin";
  const canView = canAct || activeUser?.role === "SalesManager";

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      if (tab === "transit_hold") { setTasks([]); setSalesById({}); return; }
      const taskType = tab === "vin" ? "PDI_VIN" : tab === "ready" ? "PDI_READY" : null;
      const params = { location_ids: locParam, status: "pending", with_sla: "true", ...buildApiParams(filters) };
      if (taskType) params.type = taskType;
      else params.role = "PDI";
      const ts = await Tasks.list(params);
      if (cancelled) return;
      const ids = [...new Set(ts.map((t) => t.sale_id))];
      const sales = ids.length ? await Sales.byIds(ids) : [];
      if (cancelled) return;
      setSalesById(Object.fromEntries(sales.map((s) => [s.id, s])));
      setTasks(ts);
    })();
    return () => { cancelled = true; };
  }, [tab, locParam, canView, filters]);

  // Transit-hold count for tab badge
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        const sales = await Sales.list({ transit_only: true, location_ids: locParam });
        if (!cancelled) setTransitCount(sales.length);
      } catch {
        if (!cancelled) setTransitCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [locParam, canView, tab]);

  // Ready Today count
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        const sales = await Sales.list({ location_ids: locParam, status: "ready_for_delivery" });
        if (cancelled) return;
        const today = new Date().toISOString().slice(0, 10);
        const c = (sales || []).filter((s) => (s.updated_at || s.ready_for_delivery_at || "").slice(0, 10) === today).length;
        setReadyTodayCount(c);
      } catch {
        if (!cancelled) setReadyTodayCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [locParam, canView]);

  // Global PDI workload — drives the KPI cards. Independent of tab + filter
  // bar so the numbers stay stable as the user switches tabs.
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      const ts = await Tasks.list({
        location_ids: locParam,
        role: "PDI",
        status: "pending",
        with_sla: "true",
      });
      if (cancelled) return;
      setAllPdiTasks(ts);
      const ids = [...new Set(ts.map((t) => t.sale_id))];
      const sales = ids.length ? await Sales.byIds(ids) : [];
      if (cancelled) return;
      setAllPdiSalesById(Object.fromEntries(sales.map((s) => [s.id, s])));
    })();
    return () => { cancelled = true; };
  }, [locParam, canView, tab]); // refresh on tab switch to pick up completions

  const rows = useMemo(() => {
    const enriched = tasks.map((t) => ({
      task: t,
      sale: salesById[t.sale_id],
    }));
    if (tab === "today") {
      const today = new Date().toISOString().slice(0, 10);
      return enriched.filter((r) => r.sale?.delivery?.promised_date === today);
    }
    if (tab === "overdue") {
      return enriched.filter((r) => (r.task.sla?.remaining_minutes ?? 1) < 0);
    }
    return enriched;
  }, [tasks, salesById, tab]);

  const kpi = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const src = allPdiTasks;
    const srcSales = allPdiSalesById;
    const vinPending = src.filter((t) => t.type === "PDI_VIN");
    const readyPending = src.filter((t) => t.type === "PDI_READY");
    const overdueTasks = src.filter((t) => (t.sla?.remaining_minutes ?? 1) < 0);
    const todaysDeliveries = Object.values(srcSales).filter(
      (s) => s?.delivery?.promised_date === today,
    ).length;
    const oldestPendingHelper = (list) => {
      if (list.length === 0) return "No pending tasks";
      const oldest = [...list].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))[0];
      const mins = (Date.now() - new Date(oldest.created_at || Date.now())) / 60000;
      return `Oldest pending: ${fmtMins(Math.max(0, mins))}`;
    };
    const oldestOverdueHelper = () => {
      if (overdueTasks.length === 0) return "No overdue tasks";
      const worst = [...overdueTasks].sort((a, b) => (a.sla?.remaining_minutes ?? 0) - (b.sla?.remaining_minutes ?? 0))[0];
      return `Oldest: +${fmtMins(worst.sla?.remaining_minutes ?? 0)}`;
    };
    return {
      vin: vinPending.length, vinHelper: oldestPendingHelper(vinPending),
      ready: readyPending.length, readyHelper: oldestPendingHelper(readyPending),
      today: todaysDeliveries,
      overdue: overdueTasks.length, overdueHelper: oldestOverdueHelper(),
      readyToday: readyTodayCount,
    };
  }, [allPdiTasks, allPdiSalesById, readyTodayCount]);

  if (!canView) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800">
        Switch to a PDI, Admin or Sales Manager user to access this workspace.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pdi-queue-page">
      {/* Header */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
          PDI Team · Dashboard
        </div>
        <h1 className="mt-0.5 font-display text-2xl font-bold tracking-tight">
          Hi, {userName} <span aria-hidden="true">👋</span>
        </h1>
        <p className="mt-0.5 text-xs text-slate-500">Here's your PDI overview and tasks.</p>
      </div>

      {/* KPI cards — 2-up mobile · 5-up desktop · equal h+w */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard label="Pending VIN" value={kpi.vin} helper={kpi.vinHelper}
                 Icon={Wrench} tone="amber" testid="kpi-pending-vin" />
        <KpiCard label="Pending Readiness" value={kpi.ready} helper={kpi.readyHelper}
                 Icon={BadgeCheck} tone="emerald" testid="kpi-pending-readiness" />
        <KpiCard label="Ready Today" value={kpi.readyToday} helper="Ready for delivery today"
                 Icon={BadgeCheck} tone="emerald" testid="kpi-ready-today" />
        <KpiCard label="Today's Deliveries" value={kpi.today} helper="Deliveries scheduled today"
                 Icon={Truck} tone="indigo" testid="kpi-today-deliveries" />
        <KpiCard label="Overdue" value={kpi.overdue} helper={kpi.overdueHelper}
                 Icon={AlertTriangle} tone="rose" testid="kpi-overdue" />
      </div>

      {/* Filter bar */}
      <div className="overflow-x-auto md:overflow-visible">
        <FilterBar
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(defaultFilters())}
          rightSlot={
            <ExportButton
              endpoint="tasks"
              params={exportParams}
              prefix={tab === "vin" ? "PDI_VIN_Pending" : tab === "ready" ? "PDI_Ready_Pending" : "PDI_Tasks"}
              scope={exportScope}
              monthLabel={deriveMonthLabel(filters)}
            />
          }
        />
      </div>

      {/* Tabs + cards */}
      <Card className="border-slate-200 shadow-od-subtle">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <Tabs value={tab} onValueChange={setTab}>
            <div className="sticky top-0 z-10 -mx-3 -mt-3 sm:static sm:m-0 bg-white px-3 pt-3 pb-2 sm:p-0 border-b border-slate-100 sm:border-0 overflow-x-auto">
              <TabsList data-testid="pdi-tabs"
                className="w-max h-9 p-1 bg-slate-100/80">
                <TabsTrigger value="vin" data-testid="pdi-tab-vin"
                  className="text-[12px] data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary data-[state=active]:font-bold">
                  VIN Assignment
                </TabsTrigger>
                <TabsTrigger value="ready" data-testid="pdi-tab-ready"
                  className="text-[12px] data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary data-[state=active]:font-bold">
                  Vehicle Readiness
                </TabsTrigger>
                <TabsTrigger value="today" data-testid="pdi-tab-today"
                  className="text-[12px] data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary data-[state=active]:font-bold">
                  Today's Deliveries
                </TabsTrigger>
                <TabsTrigger value="overdue" data-testid="pdi-tab-overdue"
                  className="text-[12px] data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-rose-600 data-[state=active]:font-bold">
                  Overdue
                </TabsTrigger>
                <TabsTrigger value="transit_hold" data-testid="pdi-tab-transit-hold"
                  className="text-[12px] data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-700 data-[state=active]:font-bold">
                  Transit Hold{transitCount > 0 ? ` (${transitCount})` : ""}
                </TabsTrigger>
              </TabsList>
            </div>
          </Tabs>

          {tab === "transit_hold" ? (
            <TransitHoldList
              locParam={locParam}
              locMap={locMap}
              userMap={usersById}
              canRelease={false}
              testidPrefix="pdi"
              onCountChange={setTransitCount}
            />
          ) : rows.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              No tasks here. Great work!
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {rows.map(({ task, sale }) => (
                <QueueCard
                  key={task.id}
                  task={task}
                  sale={sale}
                  loc={locMap[task.location_id]}
                  dseName={usersById[sale?.dse_id]?.name}
                  onOpen={() => navigate(`/pdi/${task.sale_id}`)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overdue footer banner */}
      {tab !== "overdue" && kpi.overdue > 0 && (
        <Card className="border-rose-200 bg-rose-50/60 p-3" data-testid="pdi-overdue-banner">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-rose-800">
              <AlertTriangle className="h-4 w-4" />
              <span><strong>{kpi.overdue}</strong> vehicle{kpi.overdue > 1 ? "s" : ""} overdue for delivery readiness</span>
            </div>
            <button type="button" onClick={() => setTab("overdue")}
              className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 hover:underline"
              data-testid="view-overdue-btn">
              View overdue <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}
